import { useState, useEffect } from "react";
import { WorkflowData } from "../WorkflowEditor";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Clock, GitBranch, Eye, RotateCcw, User } from "lucide-react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface WorkflowVersion {
  id: string;
  workflow_id: string;
  version_number: number;
  workflow_data: any;
  change_notes: string;
  created_by: string;
  created_at: string;
  profiles?: {
    first_name: string;
    last_name: string;
  };
}

interface VersionHistoryTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function VersionHistoryTab({ data, onChange }: VersionHistoryTabProps) {
  const { toast } = useToast();
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewVersion, setPreviewVersion] = useState<WorkflowVersion | null>(null);

  useEffect(() => {
    if (data.id) {
      loadVersions();
    }
  }, [data.id]);

  async function loadVersions() {
    if (!data.id) return;
    
    setLoading(true);
    try {
      const { data: versionsData, error } = await supabase
        .from('workflow_versions')
        .select('*')
        .eq('workflow_id', data.id)
        .order('version_number', { ascending: false });

      if (error) throw error;

      // Map to expected format
      const mappedVersions = (versionsData || []).map(v => ({
        id: v.id,
        workflow_id: v.workflow_id,
        version_number: v.version_number,
        workflow_data: v,
        change_notes: v.change_notes || '',
        created_by: v.changed_by || '',
        created_at: v.created_at,
      })) as WorkflowVersion[];

      setVersions(mappedVersions);
    } catch (error: any) {
      console.error('Error loading versions:', error);
      toast({
        title: "Error",
        description: "Failed to load version history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleRollback(version: WorkflowVersion) {
    if (!confirm(`Rollback to version ${version.version_number}? This will create a new version with the old data.`)) {
      return;
    }

    try {
      // Create new version with old data
      const newVersion = version.version_number + 1;
      
      onChange({
        ...data,
        ...version.workflow_data,
        version: newVersion,
        change_notes: `Rolled back to version ${version.version_number}`,
      });

      toast({
        title: "Version Restored",
        description: `Workflow rolled back to version ${version.version_number}. Don't forget to save!`,
      });
    } catch (error: any) {
      console.error('Error rolling back:', error);
      toast({
        title: "Error",
        description: "Failed to rollback version",
        variant: "destructive",
      });
    }
  }

  if (!data.id) {
    return (
      <Card className="p-8 text-center">
        <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">
          Version history will be available after saving the workflow
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold">Version History</h2>
        <p className="text-sm text-muted-foreground">
          View previous versions and rollback if needed
        </p>
      </div>

      {loading ? (
        <Card className="p-8 text-center text-muted-foreground">
          Loading version history...
        </Card>
      ) : versions.length === 0 ? (
        <Card className="p-8 text-center">
          <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No version history yet. Changes will be tracked after the first save.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {versions.map((version) => (
            <Card key={version.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={version.version_number === data.version ? "default" : "outline"}>
                      Version {version.version_number}
                    </Badge>
                    {version.version_number === data.version && (
                      <Badge variant="secondary">Current</Badge>
                    )}
                  </div>

                  <p className="text-sm font-medium mb-1">
                    {version.change_notes || "No change notes"}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {version.profiles?.first_name} {version.profiles?.last_name}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {format(new Date(version.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewVersion(version)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {version.version_number !== data.version && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRollback(version)}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={!!previewVersion} onOpenChange={() => setPreviewVersion(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Version {previewVersion?.version_number} Preview
            </DialogTitle>
          </DialogHeader>

          {previewVersion && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Title</p>
                <p className="text-lg font-semibold">{previewVersion.workflow_data.title}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Description</p>
                <p className="text-sm">{previewVersion.workflow_data.description}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Phases</p>
                <div className="space-y-2">
                  {previewVersion.workflow_data.phases?.map((phase: any, index: number) => (
                    <Card key={index} className="p-3">
                      <p className="font-medium text-sm">
                        Phase {index + 1}: {phase.phase_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{phase.description}</p>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Change Notes</p>
                <p className="text-sm">{previewVersion.change_notes || "No notes"}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
